import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchStickerPacks,
  createStickerPack,
  deleteStickerPack,
  fetchStickers,
  createSticker,
  deleteSticker,
} from '../services/api';
import { getUserRole, getUserPermissions } from '../services/authService';

const emptyPack = {
  pack_id: '', pack_name: '', pack_img_url: '', pack_url: '', pack_size: 0, is_animated: false, is_premium: false,
};
const emptySticker = {
  sticker_id: '', sticker_name: '', sticker_url: '', is_animated: false, is_premium: false,
};

const Stickers = () => {
  const isAdmin = getUserRole() === 'admin';
  const permissions = getUserPermissions();
  const canEdit = isAdmin || permissions?.stickers?.edit;
  const canDelete = isAdmin || permissions?.stickers?.delete;

  const [packs, setPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [stickers, setStickers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null); // { type: 'pack' | 'sticker', values }
  const [saving, setSaving] = useState(false);

  const loadPacks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setPacks(await fetchStickerPacks());
    } catch (err) {
      setError(err.message);
      setPacks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStickers = useCallback(async (packId) => {
    setLoading(true);
    setError('');
    try {
      setStickers(await fetchStickers(packId));
    } catch (err) {
      setError(err.message);
      setStickers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPacks();
  }, [loadPacks]);

  useEffect(() => {
    if (selectedPack) loadStickers(selectedPack.pack_id);
  }, [selectedPack, loadStickers]);

  const handleFormChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      values: { ...prev.values, [name]: type === 'checkbox' ? checked : value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (form.type === 'pack') {
        await createStickerPack({ ...form.values, pack_size: Number(form.values.pack_size) });
        await loadPacks();
      } else {
        await createSticker(selectedPack.pack_id, form.values);
        await loadStickers(selectedPack.pack_id);
      }
      setForm(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePack = async (pack) => {
    if (!window.confirm(`Delete pack "${pack.pack_name}" and all of its stickers?`)) return;
    setError('');
    try {
      await deleteStickerPack(pack.pack_id);
      if (selectedPack?.pack_id === pack.pack_id) setSelectedPack(null);
      await loadPacks();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteSticker = async (sticker) => {
    if (!window.confirm(`Delete sticker "${sticker.sticker_name}"?`)) return;
    setError('');
    try {
      await deleteSticker(sticker.sticker_id);
      await loadStickers(selectedPack.pack_id);
    } catch (err) {
      setError(err.message);
    }
  };

  const yesNo = (value) => (value ? 'Yes' : 'No');

  return (
    <div className="page-container">
      <h2 className="page-title">
        {selectedPack ? `Stickers in ${selectedPack.pack_name}` : 'Sticker Packs'}
      </h2>

      <div className="filter-form">
        {selectedPack && (
          <button type="button" className="rate-btn rate-btn-secondary" onClick={() => setSelectedPack(null)}>
            Back to packs
          </button>
        )}
        {canEdit && (
          <button
            type="button"
            className="rate-btn"
            onClick={() => setForm(selectedPack
              ? { type: 'sticker', values: emptySticker }
              : { type: 'pack', values: emptyPack })}
          >
            {selectedPack ? 'Add sticker' : 'Add pack'}
          </button>
        )}
      </div>

      {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}

      <div className="table-container">
        {!selectedPack ? (
          <table className="call-logs-table">
            <thead>
              <tr className="table-header-row">
                <th className="table-header">PREVIEW</th>
                <th className="table-header">PACK ID</th>
                <th className="table-header">NAME</th>
                <th className="table-header">SIZE</th>
                <th className="table-header">ANIMATED</th>
                <th className="table-header">PREMIUM</th>
                <th className="table-header">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="loading-cell">Loading...</td></tr>
              ) : packs.length === 0 ? (
                <tr><td colSpan="7" className="empty-cell">No sticker packs found</td></tr>
              ) : (
                packs.map((pack) => (
                  <tr key={pack.pack_id} className="table-row">
                    <td className="table-cell">
                      <img src={pack.pack_img_url} alt={pack.pack_name} width="40" height="40" />
                    </td>
                    <td className="table-cell">{pack.pack_id}</td>
                    <td className="table-cell">{pack.pack_name}</td>
                    <td className="table-cell">{pack.pack_size}</td>
                    <td className="table-cell">{yesNo(pack.is_animated)}</td>
                    <td className="table-cell">{yesNo(pack.is_premium)}</td>
                    <td className="table-cell">
                      <button type="button" className="rate-btn" onClick={() => setSelectedPack(pack)}>
                        Stickers
                      </button>{' '}
                      {canDelete && (
                        <button type="button" className="rate-btn rate-btn-danger" onClick={() => handleDeletePack(pack)}>
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        ) : (
          <table className="call-logs-table">
            <thead>
              <tr className="table-header-row">
                <th className="table-header">PREVIEW</th>
                <th className="table-header">STICKER ID</th>
                <th className="table-header">NAME</th>
                <th className="table-header">ANIMATED</th>
                <th className="table-header">PREMIUM</th>
                <th className="table-header">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="loading-cell">Loading...</td></tr>
              ) : stickers.length === 0 ? (
                <tr><td colSpan="6" className="empty-cell">No stickers in this pack</td></tr>
              ) : (
                stickers.map((sticker) => (
                  <tr key={sticker.sticker_id} className="table-row">
                    <td className="table-cell">
                      <img src={sticker.sticker_url} alt={sticker.sticker_name} width="40" height="40" />
                    </td>
                    <td className="table-cell">{sticker.sticker_id}</td>
                    <td className="table-cell">{sticker.sticker_name}</td>
                    <td className="table-cell">{yesNo(sticker.is_animated)}</td>
                    <td className="table-cell">{yesNo(sticker.is_premium)}</td>
                    <td className="table-cell">
                      {canDelete && (
                        <button type="button" className="rate-btn rate-btn-danger" onClick={() => handleDeleteSticker(sticker)}>
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {form && (
        <div className="sticker-modal-backdrop">
          <form className="sticker-modal" onSubmit={handleSubmit}>
            <h3>{form.type === 'pack' ? 'Add sticker pack' : `Add sticker to ${selectedPack.pack_name}`}</h3>
            {Object.keys(form.values).map((name) => {
              const value = form.values[name];
              const isBool = typeof value === 'boolean';
              return (
                <div className="sticker-modal-field" key={name}>
                  <label htmlFor={name}>{name}</label>
                  <input
                    id={name}
                    name={name}
                    type={isBool ? 'checkbox' : name === 'pack_size' ? 'number' : 'text'}
                    {...(isBool ? { checked: value } : { value, required: true, min: 0 })}
                    onChange={handleFormChange}
                  />
                </div>
              );
            })}
            <div className="sticker-modal-actions">
              <button type="button" className="rate-btn rate-btn-secondary" onClick={() => setForm(null)}>Cancel</button>
              <button type="submit" className="rate-btn" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Stickers;
